import React, { useRef } from "react";
import { toast } from "react-toastify";
import AiIcon from "@/assets/svg/Ai-Icon";
import FloatingProgressToast from "@/shared/components/ui/Toast/FloatingProgressToast";
import {
  confirmEditLearning,
  ignoreEditLearning,
} from "@/features/campaigns/api/campaignStepsApi";
import { showError, showInfo } from "@/shared/utils/toast";

// Lives in the app-wide toast container, so it stays up while the user moves between pages.
// The server saves the proposal on its own a moment after the countdown, so it is kept even
// if the tab closes; Confirm (or the countdown ending) just saves it right away.
function EditLearningToastPanel({ proposal, closeToast }) {
  const settled = useRef(false);
  const settle = (action) => {
    if (settled.current) return;
    settled.current = true;
    closeToast();
    action();
  };

  const confirm = () =>
    settle(() =>
      confirmEditLearning(proposal.id).catch(() =>
        showError("Couldn't save what the AI learned. Please try again."),
      ),
    );

  const ignore = () =>
    settle(() =>
      ignoreEditLearning(proposal.id).catch((error) => {
        if (error?.response?.status === 409) {
          showInfo("Already saved", "You can delete it in AI Trainer.");
        } else {
          showError("Couldn't ignore this learning. It may still be saved.");
        }
      }),
    );

  return (
    <FloatingProgressToast
      icon={
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-secondary-ultra-light text-secondary">
          <AiIcon />
        </span>
      }
      title="Learned from your edit"
      subtitle="Saves to your AI memory unless you ignore it."
      bodyClassName="px-4 pt-3 pb-4"
    >
      <ul className="space-y-2">
        {proposal.messages.map((message) => (
          <li
            key={message}
            className="text-[13px] leading-relaxed text-gray-700 dark:text-gray-300"
          >
            {message}
          </li>
        ))}
      </ul>
      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          onClick={ignore}
          className="rounded-lg border border-red-200 px-3.5 py-1.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 dark:border-red-900/60 dark:text-red-400 dark:hover:bg-red-950/40"
        >
          Ignore
        </button>
        <button
          type="button"
          onClick={confirm}
          className="relative overflow-hidden rounded-lg bg-secondary px-3.5 py-1.5 text-xs font-semibold text-white"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 origin-left bg-white/30"
            style={{
              animation: `edit-learning-countdown ${proposal.countdownMs}ms linear forwards`,
            }}
            onAnimationEnd={confirm}
          />
          <span className="relative">Confirm</span>
        </button>
      </div>
    </FloatingProgressToast>
  );
}

/** Shown only when the AI decided to save something (proposal from proposeEditLearning). */
export function showEditLearningToast(proposal) {
  if (!proposal?.id || !proposal.messages?.length) return;
  toast(
    ({ closeToast }) => (
      <EditLearningToastPanel proposal={proposal} closeToast={closeToast} />
    ),
    {
      toastId: `edit-learning-${proposal.id}`,
      position: "top-right",
      autoClose: false,
      closeOnClick: false,
      closeButton: false,
      draggable: false,
      hideProgressBar: true,
      icon: false,
      className:
        "!bg-transparent !shadow-none !p-0 !border-0 !min-h-0 !w-max !max-w-none",
    },
  );
}
